import StorefrontExperience from './components/website/StorefrontExperience';
import Media from './pages/Media';
import Storefronts from './pages/Storefronts';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import PrivateRoute from './routes/PrivateRoute';
import Login from './components/Login';
import Dashboard from './pages/Dashboard';
import Leads from './pages/Leads';
import EditLead from './pages/EditLead';
import NewLead from './pages/NewLead'; // Import NewLead page
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import CreateQuotation from './components/CreateQuotation'; // Import CreateQuotation page
import Layout from './components/Layout';
import './assets/styles/global.scss';
import Users from './pages/Users';
import UserAccessManagement from './pages/UserAccessManagement';
import NotificationCenter from './pages/NotificationCenter';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Quotations from './pages/Quotations';
// import AddOrderForm from './components/AddOrderForm';
// import WorkOrderDetail from './pages/workOrderDetails';
import QuotationSettings from './pages/QuotationSettings';
// import ProductList from './pages/ProductList';
import ProductPage from './pages/ProductPage';
import ProductDetails from './pages/ProductDetails';
import ProductEditorPage from './pages/ProductEditorPage';
import CategoriesPage from './pages/CategoriesPage';
import AttributesPage from './pages/AttributesPage';
import BuildBoxSettings from './pages/BuildBoxSettings';
import NewQuotation from './pages/NewQuotation';
import QuotationView from './pages/QuotationView';
import WorkOrderDetail from './pages/WorkOrderDetail';
import Invoices from './pages/Invoices';
import InvoiceView from './pages/InvoiceView';
import InvoiceSettings from './pages/InvoiceSettings';
import CreateInvoice from './pages/CreateInvoice';
import ProformaInvoices from './pages/ProformaInvoices';
import ProformaInvoiceView from './pages/ProformaInvoiceView';
import CreateProformaInvoice from './pages/CreateProformaInvoice';
import KOTBoard from './pages/KOTBoard';
import KOTSettings from './pages/KOTSettings';
import DeliveryBoard from './pages/DeliveryBoard';
import CouponsPage from './pages/Coupons';
import Reports from './pages/Reports';
import PaymentReminders from './pages/PaymentReminders';
import PaymentReminderSettings from './pages/PaymentReminderSettings';
import Payments from './pages/Payments';
import PaymentHistory from './pages/PaymentHistory';
import OrderFeedbacks from './pages/OrderFeedbacks';
import OrderFeedbackSettings from './pages/OrderFeedbackSettings';
import CommercialContextSettings from './pages/CommercialContextSettings';
import CheckoutArchitectureSettings from './pages/CheckoutArchitectureSettings';
import DeveloperApiCatalog from './pages/DeveloperApiCatalog';
import SettingsHub from './pages/SettingsHub';
import EmailTemplateEditorPage from './pages/EmailTemplateEditorPage';
import { getFirstAccessibleModuleRoute } from './config/modulePermissions';
import NotFound from './pages/NotFound';
import WebsiteIndex from './pages/website/WebsiteIndex';
import PageBuilder from './pages/website/PageBuilder';
import FooterEditor from './pages/website/FooterEditor';
import PopupEditor from './pages/website/PopupEditor';
import BlogsEditor from './pages/website/BlogsEditor';
import SiteSettings from './pages/website/SiteSettings';
import ReferralOrders from './pages/website/ReferralOrders';
import HeaderEditor from './pages/website/HeaderEditor';
import StorefrontOverview from './pages/website/StorefrontOverview';
import StorefrontConfiguration from './pages/website/StorefrontConfiguration';
import StorefrontDeveloper from './pages/website/StorefrontDeveloper';
import StorefrontMenus from './pages/website/StorefrontMenus';
import StorefrontIntegrations from './pages/website/StorefrontIntegrations';
import StorefrontSeo from './pages/website/StorefrontSeo';

import { NotificationProvider } from './context/NotificationContext';
import { RouteHistoryProvider } from './context/RouteHistoryContext';


const HomeRoute = () => {
    const { currentUser, modulePermissions } = useAuth();
    return currentUser
        ? <Navigate to={getFirstAccessibleModuleRoute(modulePermissions)} />
        : <Login />;
};

const QuotationsTypoRedirect = () => {
    const location = useLocation();
    const normalizedPath = location.pathname
        .replace(/^\/qoutations(\/|$)/, '/quotations$1')
        .replace(/^\/qoutation(\/|$)/, '/quotation$1');

    return <Navigate to={`${normalizedPath}${location.search}${location.hash}`} replace />;
};

const LegacyPathNormalizer = () => {
    const location = useLocation();
    const navigate = useNavigate();

    React.useEffect(() => {
        const { pathname, search, hash } = location;
        let normalizedPath = pathname;

        normalizedPath = normalizedPath.replace(/^\/qoutations(\/|$)/, '/quotations$1');
        normalizedPath = normalizedPath.replace(/^\/qoutation(\/|$)/, '/quotation$1');

        if (normalizedPath !== pathname) {
            navigate(`${normalizedPath}${search}${hash}`, { replace: true });
        }
    }, [location, navigate]);

    return null;
};

const App = () => {
    console.log("🚀 App rendered");
    return (
        <AuthProvider>
            <NotificationProvider>
                <Router>
                    <RouteHistoryProvider>
                        <LegacyPathNormalizer />
                        <Routes>
                            <Route path="/" element={<HomeRoute />} />
                            <Route path="/storefronts/:storefrontKey/experience/content/page/:pageId" element={<PrivateRoute requiredModule="website"><PageBuilder /></PrivateRoute>} />
                            <Route path="/storefronts/:storefrontKey/experience/page/:pageId" element={<PrivateRoute requiredModule="website"><PageBuilder /></PrivateRoute>} />
                            <Route path="/website/page/:pageId" element={<PrivateRoute requiredModule="website"><PageBuilder /></PrivateRoute>} />
                            <Route element={<Layout />}>
                                <Route
                                    path="/dashboard"
                                    element={
                                        <PrivateRoute requiredModule="dashboard">
                                            <Dashboard />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/invoices"
                                    element={
                                        <PrivateRoute requiredModule="invoices">
                                            <Invoices />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/invoices/:id"
                                    element={
                                        <PrivateRoute requiredModule="invoices">
                                            <InvoiceView />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/invoices/create"
                                    element={
                                        <PrivateRoute requiredModule="invoices">
                                            <CreateInvoice />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/proforma-invoices"
                                    element={
                                        <PrivateRoute requiredModule="invoices">
                                            <ProformaInvoices />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/proforma-invoices/:id"
                                    element={
                                        <PrivateRoute requiredModule="invoices">
                                            <ProformaInvoiceView />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/proforma-invoices/create"
                                    element={
                                        <PrivateRoute requiredModule="invoices">
                                            <CreateProformaInvoice />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/invoice-settings"
                                    element={
                                        <PrivateRoute requiredModule="invoices">
                                            <InvoiceSettings />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/payment-reminders"
                                    element={
                                        <PrivateRoute requiredModule="payment_reminders">
                                            <PaymentReminders />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/payment-reminders/settings"
                                    element={
                                        <PrivateRoute requiredModule="payment_reminders">
                                            <PaymentReminderSettings />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/payments"
                                    element={
                                        <PrivateRoute requiredModule="payments">
                                            <Payments />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/payments/history"
                                    element={
                                        <PrivateRoute requiredModule="payments">
                                            <PaymentHistory />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/leads"
                                    element={
                                        <PrivateRoute requiredModule="leads">
                                            <Leads />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/customers"
                                    element={
                                        <PrivateRoute requiredModule="customers">
                                            <Customers />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/customers/detail"
                                    element={
                                        <PrivateRoute requiredModule="customers">
                                            <CustomerDetail />
                                        </PrivateRoute>
                                    }
                                />
                                
                                <Route
                                    path="/leads/new"  // New route for creating a lead
                                    element={
                                        <PrivateRoute requiredModule="leads">
                                            <NewLead />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/leads/:id/edit"
                                    element={
                                        <PrivateRoute requiredModule="leads">
                                            <EditLead />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/quotation/create/:leadId"  // New route for creating a quotation
                                    element={
                                        <PrivateRoute requiredModule="quotations">
                                            <CreateQuotation />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/quotation-create"  // New route for creating a quotation
                                    element={
                                        <PrivateRoute requiredModule="quotations">
                                            <NewQuotation />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/users"  // New route for creating a quotation
                                    element={
                                        <PrivateRoute requiredModule="users">
                                            <Users />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/users/access"
                                    element={
                                        <PrivateRoute requiredModule="users">
                                            <UserAccessManagement />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/notifications"
                                    element={
                                        <PrivateRoute requiredModule="dashboard">
                                            <NotificationCenter />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/orders"
                                    element={
                                        <PrivateRoute requiredModule="orders">
                                            <Orders />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/orders/:id"
                                    element={
                                        <PrivateRoute requiredModule="orders">
                                            <OrderDetail />
                                        </PrivateRoute>
                                    }
                                />
                                <Route path="/workorders" element={<PrivateRoute requiredModule="orders"><Navigate to="/orders" replace /></PrivateRoute>} />
                                <Route path="/workorders/create" element={<PrivateRoute requiredModule="orders"><Navigate to="/orders" replace /></PrivateRoute>} />
                                <Route
                                    path="/workorders/:id"
                                    element={
                                        <PrivateRoute requiredModule="orders">
                                            <WorkOrderDetail />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/kots"
                                    element={
                                        <PrivateRoute requiredModule="kots">
                                            <KOTBoard />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/kots/settings"
                                    element={
                                        <PrivateRoute requiredModule="kots">
                                            <KOTSettings />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/deliveries"
                                    element={
                                        <PrivateRoute requiredModule="deliveries">
                                            <DeliveryBoard />
                                        </PrivateRoute>
                                    }
                                />

                                <Route path="/settings/communications/templates/:id" element={<PrivateRoute requiredModule="settings"><EmailTemplateEditorPage /></PrivateRoute>} />
                                <Route
                                    path="/settings"  // New route for creating a quotation
                                    element={
                                        <PrivateRoute requiredModule="settings">
                                            <SettingsHub />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/settings/*"
                                    element={
                                        <PrivateRoute requiredModule="settings">
                                            <SettingsHub />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/developer/api-catalog"
                                    element={
                                        <PrivateRoute requiredModule="settings">
                                            <DeveloperApiCatalog />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/commercial-context"
                                    element={
                                        <PrivateRoute requiredModule="settings">
                                            <CommercialContextSettings />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/checkout-architecture"
                                    element={
                                        <PrivateRoute requiredModule="settings">
                                            <CheckoutArchitectureSettings />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/coupons"
                                    element={
                                        <PrivateRoute requiredModule="settings">
                                            <CouponsPage />
                                        </PrivateRoute>
                                    }
                                />

                                {/* Website / CMS */}
                                <Route path="/media" element={<PrivateRoute requiredModule="dashboard"><Media /></PrivateRoute>} />
                                <Route path="/storefronts" element={<PrivateRoute requiredModule="website"><Storefronts /></PrivateRoute>} />
                                <Route path="/storefronts/:storefrontKey/experience" element={<PrivateRoute requiredModule="website"><StorefrontExperience><StorefrontOverview /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/storefronts/:storefrontKey/experience/content/pages" element={<PrivateRoute requiredModule="website"><StorefrontExperience><WebsiteIndex /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/storefronts/:storefrontKey/experience/content/blogs" element={<PrivateRoute requiredModule="website"><StorefrontExperience><BlogsEditor /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/storefronts/:storefrontKey/experience/content/popups" element={<PrivateRoute requiredModule="website"><StorefrontExperience><PopupEditor /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/storefronts/:storefrontKey/experience/navigation/header" element={<PrivateRoute requiredModule="website"><StorefrontExperience><HeaderEditor /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/storefronts/:storefrontKey/experience/navigation/menus" element={<PrivateRoute requiredModule="website"><StorefrontExperience><StorefrontMenus /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/storefronts/:storefrontKey/experience/navigation/footer" element={<PrivateRoute requiredModule="website"><StorefrontExperience><FooterEditor /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/storefronts/:storefrontKey/experience/seo" element={<PrivateRoute requiredModule="website"><StorefrontExperience><StorefrontSeo /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/storefronts/:storefrontKey/experience/configuration" element={<PrivateRoute requiredModule="website"><StorefrontExperience><StorefrontConfiguration /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/storefronts/:storefrontKey/experience/integrations" element={<PrivateRoute requiredModule="website"><StorefrontExperience><StorefrontIntegrations /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/storefronts/:storefrontKey/experience/developer" element={<PrivateRoute requiredModule="website"><StorefrontExperience><StorefrontDeveloper /></StorefrontExperience></PrivateRoute>} />
                                {/* Compatibility aliases for bookmarks created during the first storefront normalization pass. */}
                                <Route path="/storefronts/:storefrontKey/experience/pages" element={<PrivateRoute requiredModule="website"><StorefrontExperience><WebsiteIndex /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/storefronts/:storefrontKey/experience/header" element={<PrivateRoute requiredModule="website"><StorefrontExperience><HeaderEditor /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/storefronts/:storefrontKey/experience/footer" element={<PrivateRoute requiredModule="website"><StorefrontExperience><FooterEditor /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/storefronts/:storefrontKey/experience/blogs" element={<PrivateRoute requiredModule="website"><StorefrontExperience><BlogsEditor /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/storefronts/:storefrontKey/experience/popup" element={<PrivateRoute requiredModule="website"><StorefrontExperience><PopupEditor /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/storefronts/:storefrontKey/experience/settings" element={<PrivateRoute requiredModule="website"><StorefrontExperience><StorefrontConfiguration /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/website" element={<PrivateRoute requiredModule="website"><StorefrontExperience><WebsiteIndex /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/website/pages" element={<PrivateRoute requiredModule="website"><StorefrontExperience><WebsiteIndex /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/website/referrals" element={<PrivateRoute requiredModule="website"><ReferralOrders /></PrivateRoute>} />
                                <Route path="/referrals" element={<PrivateRoute requiredModule="settings"><ReferralOrders /></PrivateRoute>} />
                                <Route path="/website/header" element={<PrivateRoute requiredModule="website"><StorefrontExperience><HeaderEditor /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/website/footer" element={<PrivateRoute requiredModule="website"><StorefrontExperience><FooterEditor /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/website/popup" element={<PrivateRoute requiredModule="website"><StorefrontExperience><PopupEditor /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/website/blogs" element={<PrivateRoute requiredModule="website"><StorefrontExperience><BlogsEditor /></StorefrontExperience></PrivateRoute>} />
                                <Route path="/website/settings" element={<PrivateRoute requiredModule="website"><StorefrontExperience><SiteSettings /></StorefrontExperience></PrivateRoute>} />

                                <Route
                                    path="/reports"  // Reports module
                                    element={
                                        <PrivateRoute requiredModule="reports">
                                            <Reports />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/feedbacks"
                                    element={
                                        <PrivateRoute requiredModule="reports">
                                            <OrderFeedbacks />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/feedbacks/settings"
                                    element={
                                        <PrivateRoute requiredModule="reports">
                                            <OrderFeedbackSettings />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/quotations"  // New route for creating a quotation
                                    element={
                                        <PrivateRoute requiredModule="quotations">
                                            <Quotations />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/quotations/:id"
                                    element={
                                        <PrivateRoute requiredModule="quotations">
                                            <QuotationView />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/quotations-settings"  // New route for creating a quotation
                                    element={
                                        <PrivateRoute requiredModule="quotations">
                                            <QuotationSettings />
                                        </PrivateRoute>
                                    }
                                />

                                <Route path="/products" element={<Navigate to="/products/list" replace />} />

                                <Route
                                    path="/products/list"
                                    element={
                                        <PrivateRoute requiredModule="products">
                                            <ProductPage />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/products/:id"
                                    element={
                                        <PrivateRoute requiredModule="products">
                                            <ProductDetails />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/products/new"
                                    element={
                                        <PrivateRoute requiredModule="products">
                                            <ProductEditorPage />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/products/:id/edit"
                                    element={
                                        <PrivateRoute requiredModule="products">
                                            <ProductEditorPage />
                                        </PrivateRoute>
                                    }
                                />

                                <Route path="/products/categories" element={<Navigate to="/categories" replace />} />
                                <Route path="/products/fusion-box-categories" element={<Navigate to="/build-your-box" replace />} />
                                <Route path="/products/attributes" element={<Navigate to="/attributes" replace />} />

                                <Route
                                    path="/categories"
                                    element={
                                        <PrivateRoute requiredModule="products">
                                            <CategoriesPage />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/build-your-box"
                                    element={
                                        <PrivateRoute requiredModule="products">
                                            <BuildBoxSettings />
                                        </PrivateRoute>
                                    }
                                />
                                <Route path="/fusion-box-categories" element={<Navigate to="/build-your-box" replace />} />

                                <Route
                                    path="/attributes"
                                    element={
                                        <PrivateRoute requiredModule="products">
                                            <AttributesPage />
                                        </PrivateRoute>
                                    }
                                />
                            </Route>

                            <Route path="/qoutations/*" element={<QuotationsTypoRedirect />} />
                            <Route path="/qoutation/*" element={<QuotationsTypoRedirect />} />

                            <Route path="*" element={<NotFound />} />

                        </Routes>
                    </RouteHistoryProvider>
                </Router>
            </NotificationProvider>
        </AuthProvider>
    );
};

export default App;
